<template>
  <EditableComponent
    v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
    :id="id"
    add
    collapsible
    :duplicate="componentPermissions.canDuplicate"
    edit
    :excludeStylables="[ThemeComponentOptionEnum.PADDING]"
    :remove="componentPermissions.canRemove"
    :selected="componentSelectedMode && componentSelectedMode.id === id"
    :settings="!usedInSectionId"
    stylable
    @add="setAddMode(ComponentTypes.Page, id)"
    @duplicate="duplicate(id)"
    @edit="setComponentEditMode(ComponentTypes.Page, id)"
    @remove="removeComponentById(id)"
    @select="setComponentSelectedMode(id)"
    @settings="setComponentSettingsMode(id)"
    @style="$emit('style', $event)"
  >
    <div class="rounded-2">
      <div :class="['cb-page-component relative min-h-[10vh] pa-4', { [aspectRatioClass]: !!aspectRatioClass }]">
        <video
          v-if="data?.background_video_url"
          autoplay
          class="absolute top-0 left-0 w-full h-full rounded-2"
          loop
          muted
          :src="data.background_video_url"
        />
        <div class="overflow-auto max-h-full">
          <slot />
        </div>
      </div>
    </div>
  </EditableComponent>
  <div v-else :class="['cb-page-component relative', { [aspectRatioClass]: !!aspectRatioClass }]">
    <video
      v-if="data?.background_video_url"
      autoplay
      class="absolute top-0 left-0 right-0 bottom-0 w-full h-full rounded-2"
      loop
      muted
      :src="data.background_video_url"
    />
    <div class="overflow-auto max-h-full">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { toRefs, computed } from 'vue'

import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { defaultWhenUsedInSection, aspectRatioClasses } from '@canvas-builder/constants'
import type { CanvasRetrieve } from '@openapi'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import { ComponentTypes, type ComponentNode, type PageProps } from '@canvas-builder/types/canvas'

const props = withDefaults(
  defineProps<{
    id: string
    data?: PageProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    children?: ComponentNode[]
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)
const { id, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})
const {
  isEditMode,
  componentSelectedMode,
  isViewOnlyMode,
  removeComponentById,
  duplicate,
  setComponentSelectedMode,
  setComponentSettingsMode,
  setAddMode,
  setComponentEditMode,
} = useCanvas()

const aspectRatioClass = computed(() => aspectRatioClasses[props.data?.aspect_ratio || 'unlocked'])
</script>
